import React from 'react';
import { Link } from 'react-router-dom';
import { ongoingSupportPages } from '../data/ongoingSupportPages';

const BookingLink = ({ href, children = 'Book A Session' }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="inline-flex min-h-12 items-center justify-center rounded-full bg-purple-700 px-7 py-3 font-semibold text-white shadow-md transition hover:bg-purple-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2"
  >
    {children}
  </a>
);

const OfferingCard = ({ offering, bookingUrl }) => (
  <article className="flex h-full flex-col rounded-2xl border border-purple-200 bg-white p-6 shadow-sm sm:p-8">
    <h3 className="font-title text-2xl font-semibold text-purple-900">{offering.title}</h3>
    {offering.subtitle && (
      <p className="mt-2 font-subtitle text-lg font-semibold text-amber-700">
        {offering.subtitle}
      </p>
    )}
    <p className="mt-4 flex-1 leading-relaxed text-purple-800 font-body">{offering.body}</p>
    <div className="mt-6">
      <BookingLink href={bookingUrl} />
    </div>
  </article>
);

const OngoingSupportPage = ({ practitioner }) => {
  const page = ongoingSupportPages[practitioner];
  if (!page) return null;

  return (
    <main className="min-h-screen bg-[#fbf9fc] pt-20 text-purple-950">
      <section
        className="relative overflow-hidden border-b-8 border-purple-800 px-4 py-14 text-center sm:py-20"
        style={{
          background:
            'radial-gradient(circle at 15% 20%, rgba(255,255,255,.96) 0, rgba(255,255,255,0) 24%), radial-gradient(circle at 88% 55%, rgba(255,255,255,.9) 0, rgba(255,255,255,0) 22%), linear-gradient(135deg, #fffdfd 0%, #f5eefb 55%, #fcf8f5 100%)',
        }}
      >
        <div className="relative mx-auto max-w-4xl">
          <img
            src={page.photo}
            alt={page.name}
            className="mx-auto h-52 w-52 rounded-[2.5rem] object-cover shadow-xl ring-4 ring-white sm:h-64 sm:w-64"
            width="256"
            height="256"
          />
          <p className="mt-8 font-subtitle text-xl font-bold tracking-wider text-purple-700 sm:text-2xl">
            ONGOING SUPPORT WITH
          </p>
          <h1 className="mt-2 font-title text-4xl text-purple-900 sm:text-5xl">
            {page.name}
          </h1>
          <p className="mt-3 font-subtitle text-xl font-semibold text-amber-700 sm:text-2xl">
            {page.title}
          </p>
          <p className="mx-auto mt-7 max-w-2xl font-body text-xl leading-relaxed text-purple-700 sm:text-2xl">
            {page.heroMessage}
          </p>
          {page.heroSubheading && (
            <p className="mt-4 font-subtitle text-lg font-bold tracking-[0.16em] text-purple-800 sm:text-xl">
              {page.heroSubheading}
            </p>
          )}
        </div>
      </section>

      <section className="px-4 py-14 sm:py-20">
        <div className="mx-auto max-w-6xl">
          {page.introduction && (
            <article className="mb-8 rounded-2xl border border-purple-200 bg-white p-6 shadow-sm sm:p-10">
              <p className="font-subtitle text-sm font-bold uppercase tracking-[0.18em] text-purple-600">
                {page.introduction.category}
              </p>
              <h2 className="mt-3 font-title text-3xl text-purple-900 sm:text-4xl">
                {page.introduction.title}
              </h2>
              <p className="mt-5 leading-relaxed text-purple-800 font-body sm:text-lg">
                {page.introduction.body}
              </p>
              <div className="mt-6">
                <BookingLink href={page.introduction.bookingUrl} />
              </div>
            </article>
          )}

          <div className="grid gap-6 md:grid-cols-2">
            {page.offerings.map((offering) => (
              <OfferingCard
                key={offering.title}
                offering={offering}
                bookingUrl={
                  practitioner === 'cindie'
                    ? 'https://twnhub.online/book/cindie?globalServiceId=2'
                    : 'https://twnhub.online/book/aquiera?globalServiceId=2'
                }
              />
            ))}
          </div>

          {page.hypnosis && (
            <section className="mt-14 rounded-3xl bg-purple-950 px-6 py-8 text-purple-50 sm:mt-20 sm:px-10 sm:py-12">
              <p className="font-subtitle text-sm font-bold tracking-[0.18em] text-amber-300">
                {page.hypnosis.title}
              </p>
              <h2 className="mt-2 font-title text-3xl text-white sm:text-4xl">
                {page.hypnosis.subtitle}
              </h2>
              <p className="mt-5 max-w-4xl leading-relaxed text-purple-100 font-body sm:text-lg">
                {page.hypnosis.introduction}
              </p>
              <h3 className="mt-8 font-subtitle text-lg font-bold text-amber-200">
                FOCUS AREAS INCLUDE:
              </h3>
              <div className="mt-5 grid gap-4 sm:grid-cols-2">
                {page.hypnosis.focusAreas.map((area) => (
                  <article
                    key={area.title}
                    className="rounded-xl border border-purple-700 bg-purple-900/70 p-5"
                  >
                    <h4 className="font-subtitle text-lg font-semibold text-white">
                      🌀 {area.title}
                    </h4>
                    <p className="mt-2 leading-relaxed text-purple-100 font-body">{area.body}</p>
                  </article>
                ))}
              </div>
              <div className="mt-8">
                <BookingLink href={page.hypnosis.bookingUrl}>Book A Hypnosis Consultation</BookingLink>
              </div>
              <p className="mt-5 text-sm leading-relaxed text-purple-200 font-body">
                {page.hypnosis.note}
              </p>
            </section>
          )}

          <div className="mt-12 text-center">
            <Link
              to={page.profilePath}
              className="font-semibold text-purple-800 underline decoration-purple-300 underline-offset-4 hover:text-purple-950"
            >
              ← Back to {page.name.split(',')[0]}’s Profile
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default OngoingSupportPage;
